import { CustomButton } from '@ui-kit'
import { FC, useState } from 'react'
import {
  addToaster,
  hideLoader,
  interruptWorkFlow,
  showLoader,
} from '@app-store'
import { isEmpty } from 'lodash'
import { v4 as uuid } from 'uuid'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  NAMESPACES,
  POLICY_UPDATE_CONSTANTS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { permissionObject } from '../../utils/helperFunctions'
import { STRINGS } from '../../utils/strings'
import { PolicyUpdateProcessPropsType } from '../../types/workflow.types'

const PolicyUpdateProcess: FC<PolicyUpdateProcessPropsType> = ({
  refetchWorkflow,
  tabIndex,
  isDisabled,
  heightClass,
}) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const caseData = useAppSelector((state) => state?.workflowData?.caseData)
  const caseDetails = useAppSelector(
    (state) => state?.workflowData?.caseDetails
  )
  const programPermissions = useAppSelector(
    (state) => state?.clientPermissions?.programPermissions
  )

  const policyUpdateConfig =
    workflowDefinition?.global_variables?.stepper_config?.action_buttons?.[
      POLICY_UPDATE_CONSTANTS.POLICY_UPDATE_ACTION_KEY
    ]

  const hasPermission = permissionObject.Queue_Review_Manage(
    programPermissions,
    caseData?.programs?.id?.toString() ??
      workflowDefinition?.program_id?.toString() ??
      '',
    caseData?.services?.service_name ?? ''
  )

  const isGroupVisible =
    !isEmpty(policyUpdateConfig?.group) &&
    !!workflowInstance?.current_group &&
    policyUpdateConfig?.group?.includes(workflowInstance.current_group)

  const isProcessingInBackground =
    workflowInstance?.state === WORKFLOW_STATUS.PROCESSING_IN_BACKGROUND

  const isCaseClosedOrVoid =
    caseDetails?.overallCaseStatus === STRINGS.VOID ||
    caseDetails?.overallCaseStatus === STRINGS.CLOSED

  const isButtonDisabled = isDisabled ?? isCaseClosedOrVoid

  const handlePolicyUpdate = async () => {
    const signal = policyUpdateConfig?.signal
    if (!signal || !workflowInstance?.id || isSubmitting) return

    try {
      dispatch(showLoader())
      setIsSubmitting(true)
      const interruptResponse = await interruptWorkFlow({
        workflowId: workflowInstance.id,
        signal,
      })
      if (interruptResponse?.data) {
        dispatch(
          addToaster({
            message: STRINGS.POLICY_UPDATE_SUCCESS_MSG,
            type: 'success',
            id: uuid(),
          })
        )
        refetchWorkflow?.()
        window.dispatchEvent(new CustomEvent('customRefetch'))
      }
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          message: STRINGS.POLICY_UPDATE_ERROR_MSG,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      setIsSubmitting(false)
      dispatch(hideLoader())
    }
  }

  if (!hasPermission || !isGroupVisible || !policyUpdateConfig?.signal) {
    return null
  }

  return (
    <CustomButton
      onClick={handlePolicyUpdate}
      tabIndex={tabIndex}
      height={heightClass}
      verticalPaddingClasses="py-2"
      horizontalPaddingClasses="px-4"
      disabled={isButtonDisabled || isSubmitting || isProcessingInBackground}
    >
      {NAMESPACES.POLICY_UPDATE}
    </CustomButton>
  )
}

export default PolicyUpdateProcess
